import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Plane, MapPin, Download, Loader2, Trash2 } from 'lucide-react';
import { tripService } from '../services/api';
import { useDialog } from '../context/DialogContext';
import useIsMobile from '../hooks/useIsMobile';
import BottomSheet from '../components/mobile/BottomSheet';
import PdfReadySheet from '../components/mobile/PdfReadySheet';
import { openPdfInNewTab } from '../utils/pdfDelivery';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isSameMonth, isSameDay, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';

// Solid colours for the compact mobile calendar bars (same order as TRIP_COLORS)
const TRIP_BAR_COLORS = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500'];

const TRIP_COLORS = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-green-100 text-green-800 border-green-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-teal-100 text-teal-800 border-teal-200',
];

function TripCalendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pdf, setPdf] = useState(null);
  const { toast, confirm } = useDialog();
  const isMobile = useIsMobile();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tripService.getCalendar(year, month);
      setTrips(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  // Calendar grid
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  const prevMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const colorFor = (tripId) => TRIP_COLORS[tripId % TRIP_COLORS.length];

  const handleDeleteTrip = async (tripId) => {
    if (!await confirm({
      title: 'Delete this trip?',
      message: 'This cannot be undone. Linked expenses will not be deleted.',
      confirmLabel: 'Delete',
      destructive: true,
    })) return;
    setDeleteLoading(true);
    try {
      await tripService.deleteTrip(tripId);
      setSelectedTrip(null);
      load();
    } catch (err) {
      toast('Error deleting trip', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleGeneratePdf = async (trip) => {
    setPdfLoading(true);
    try {
      const blob = await tripService.generatePdf(trip.id);
      if (isMobile) {
        const safeName = trip.name.replace(/[^\w\- ]+/g, '').replace(/\s+/g, '_') || 'trip';
        setSelectedTrip(null);
        setPdf({ blob, filename: `Travel_allowance_${safeName}.pdf` });
      } else {
        openPdfInNewTab(blob);
      }
    } catch (err) {
      toast('Error generating PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const tripsForDay = (day) =>
    trips.filter(t => {
      if (!t.start_date || !t.end_date) return false;
      const start = parseISO(t.start_date);
      const end = parseISO(t.end_date);
      return day >= start && day <= end;
    });

  const isStartDay = (trip, day) => {
    if (!trip.start_date) return false;
    return isSameDay(day, parseISO(trip.start_date));
  };

  const isEndDay = (trip, day) => {
    if (!trip.end_date) return false;
    return isSameDay(day, parseISO(trip.end_date));
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="hidden md:flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Trip Calendar</h1>
        <button
          onClick={() => navigate('/trips/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Trip
        </button>
      </div>

      {/* Month navigation */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 md:px-6 py-2 md:py-4 border-b border-gray-200">
          <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-semibold text-gray-900">
            {format(currentDate, 'MMMM yyyy', { locale: enUS })}
          </h2>
          <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-full">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase">
              {isMobile ? d[0] : d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div className="h-64 flex items-center justify-center text-gray-400">Loading...</div>
        ) : (
          <div className="grid grid-cols-7">
            {days.map((day, idx) => {
              const inMonth = isSameMonth(day, currentDate);
              const dayTrips = tripsForDay(day);
              const isToday = isSameDay(day, new Date());

              if (isMobile) {
                return (
                  <button
                    key={idx}
                    type="button"
                    disabled={dayTrips.length === 0}
                    onClick={() => setSelectedTrip(dayTrips[0])}
                    className={`h-12 border-b border-r border-gray-100 flex flex-col items-center pt-1 gap-1 ${
                      !inMonth ? 'bg-gray-50' : 'bg-white'
                    }`}
                  >
                    <span className={`text-sm w-6 h-6 flex items-center justify-center rounded-full ${
                      isToday ? 'bg-blue-600 text-white' : inMonth ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {format(day, 'd')}
                    </span>
                    <span className="flex gap-0.5 w-full px-0.5">
                      {dayTrips.slice(0, 2).map(trip => (
                        <span key={trip.id} className={`h-1.5 flex-1 rounded-full ${TRIP_BAR_COLORS[trip.id % TRIP_BAR_COLORS.length]}`} />
                      ))}
                    </span>
                  </button>
                );
              }

              return (
                <div
                  key={idx}
                  className={`min-h-[100px] border-b border-r border-gray-100 p-1 ${
                    !inMonth ? 'bg-gray-50' : 'bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-blue-600 text-white' : inMonth ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayTrips.map(trip => (
                      <div
                        key={trip.id}
                        onClick={() => setSelectedTrip(trip)}
                        className={`text-xs px-1.5 py-0.5 rounded border cursor-pointer truncate ${colorFor(trip.id)} ${
                          isStartDay(trip, day) ? 'rounded-l-full' : ''
                        } ${isEndDay(trip, day) ? 'rounded-r-full' : ''}`}
                        title={trip.name}
                      >
                        {isStartDay(trip, day) && (
                          <span className="flex items-center gap-1">
                            {trip.is_foreign_travel && <Plane className="w-3 h-3 flex-shrink-0" />}
                            <span className="truncate">{trip.name}</span>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Trip list for this month */}
      {trips.length === 0 && !loading && isMobile && (
        <p className="text-center text-sm text-gray-500 py-4">No trips this month. Tap ＋ to add one.</p>
      )}
      {trips.length > 0 && (
        <div className="bg-white shadow rounded-2xl md:rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Trips in {format(currentDate, 'MMMM yyyy', { locale: enUS })}
          </h3>
          <div className="space-y-2">
            {trips.map(trip => (
              <div
                key={trip.id}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${colorFor(trip.id)}`}
                onClick={() => setSelectedTrip(trip)}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {trip.is_foreign_travel && <Plane className="w-4 h-4 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium truncate">{trip.name}</p>
                    {trip.destinations.length > 0 && (
                      <p className="text-xs opacity-75 flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3" />
                        {trip.destinations.map(d => d.client_name).filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-xs flex-shrink-0 ml-2 text-right">
                  {trip.start_date && format(parseISO(trip.start_date), 'dd/MM')}
                  {trip.end_date && trip.start_date !== trip.end_date && (
                    <> – {format(parseISO(trip.end_date), 'dd/MM')}</>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trip detail: bottom sheet on mobile */}
      <BottomSheet
        open={isMobile && Boolean(selectedTrip)}
        onClose={() => setSelectedTrip(null)}
        title={selectedTrip?.name}
      >
        {selectedTrip && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 space-y-1">
              {selectedTrip.start_date && (
                <p>
                  {format(parseISO(selectedTrip.start_date), 'EEE d MMM', { locale: enUS })}
                  {selectedTrip.end_date && selectedTrip.end_date !== selectedTrip.start_date &&
                    ` – ${format(parseISO(selectedTrip.end_date), 'EEE d MMM yyyy', { locale: enUS })}`}
                </p>
              )}
              {selectedTrip.is_foreign_travel && (
                <p className="flex items-center gap-1.5 text-blue-700"><Plane className="w-4 h-4" /> Foreign travel</p>
              )}
              {selectedTrip.destinations.map(d => (
                <p key={d.id} className="flex items-center gap-1.5 text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {d.client_name}
                  {d.location_label && <span className="text-gray-400">· {d.location_label}</span>}
                </p>
              ))}
            </div>
            <div className="space-y-2">
              <button
                onClick={() => handleGeneratePdf(selectedTrip)}
                disabled={pdfLoading}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 text-white font-medium disabled:opacity-60"
              >
                {pdfLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                {pdfLoading ? 'Generating…' : 'Allowance PDF'}
              </button>
              <button
                onClick={() => navigate(`/trips/${selectedTrip.id}/edit`)}
                className="w-full py-3 rounded-xl border border-gray-300 text-gray-800 font-medium"
              >
                Edit trip
              </button>
              <button
                onClick={() => handleDeleteTrip(selectedTrip.id)}
                disabled={deleteLoading}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-red-600 font-medium disabled:opacity-60"
              >
                {deleteLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                Delete trip
              </button>
            </div>
          </div>
        )}
      </BottomSheet>

      <PdfReadySheet pdf={pdf} onClose={() => setPdf(null)} title="Allowance PDF ready" />

      {/* Trip detail modal (desktop) */}
      {selectedTrip && !isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedTrip(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  {selectedTrip.is_foreign_travel && <Plane className="w-5 h-5 text-blue-500" />}
                  {selectedTrip.name}
                </h3>
                {selectedTrip.start_date && (
                  <p className="text-sm text-gray-500 mt-1">
                    {format(parseISO(selectedTrip.start_date), 'dd MMM', { locale: enUS })}
                    {selectedTrip.end_date && selectedTrip.end_date !== selectedTrip.start_date &&
                      ` – ${format(parseISO(selectedTrip.end_date), 'dd MMM yyyy', { locale: enUS })}`}
                  </p>
                )}
              </div>
            </div>
            {selectedTrip.destinations.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Destinations</p>
                {selectedTrip.destinations.map(d => (
                  <div key={d.id} className="flex items-center gap-1 text-sm text-gray-700">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" />
                    {d.client_name}
                    {d.location_label && <span className="text-gray-400">({d.location_label})</span>}
                    {d.location_city && <span className="text-gray-400">— {d.location_city}</span>}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <button
                onClick={() => navigate(`/trips/${selectedTrip.id}/edit`)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
              >
                Edit trip
              </button>
              <button
                disabled={pdfLoading}
                onClick={() => handleGeneratePdf(selectedTrip)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                {pdfLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {pdfLoading ? 'Generating…' : 'Generate PDF'}
              </button>
              <button
                disabled={deleteLoading}
                onClick={() => handleDeleteTrip(selectedTrip.id)}
                className="inline-flex items-center justify-center gap-1 px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
              <button
                onClick={() => setSelectedTrip(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TripCalendar;
